/**
 * assets/dm-sticky-atc.js — `<dm-sticky-atc>`, the mobile sticky add-to-cart bar.
 *
 * Markup: `snippets/dm-pdp-sticky-bar.liquid` (read its header first — it owns
 * the "one form, always" contract). Styling: `component-dm-sticky-bar.css`.
 *
 * WHAT THIS FILE IS NOT. It never calls `fetch('/cart/add')`, never POSTs a
 * FormData, never builds a cart request and never formats money. The sticky
 * button is a `form=`-linked `[type=submit]`, so the browser submits the
 * in-page `<product-form>` and retained Dawn (`product-form.js`) does the add.
 * A second add path is the "silently adds the DEFAULT variant" defect
 * 02-pdp-mobile.md §7 opens with, and it is structurally absent here.
 *
 * ── 1. MIRROR THE IN-FORM SUBMIT BUTTON ──
 * Dawn's `ProductForm.onSubmitHandler` resolves its spinner and disabled state
 * from `this.querySelector('[type="submit"]')` — the button INSIDE
 * `<product-form>` — so a `form=`-linked button outside it gets no loading
 * state, no disabled state and no error surface. From the real source:
 *   onSubmitHandler     sets aria-disabled="true", adds `.loading`, unhides
 *                       `.loading__spinner`
 *   .finally()          removes `.loading`, removes aria-disabled unless the
 *                       response errored, re-hides the spinner
 *   toggleSubmitButton  sets/removes the `disabled` ATTRIBUTE and rewrites the
 *                       label span — called by product-info.js on every variant
 *                       change and by `setUnavailable()`
 * ONE MutationObserver, for the life of the mount, `attributeFilter`-scoped to
 * exactly `disabled` / `aria-disabled` / `class`, copies those onto the sticky
 * button. `attributes: true` unfiltered, or `subtree: true`, would put this on
 * the critical path of every DOM write Dawn makes inside the form.
 * The LABEL is copied in the same sync rather than by a second observer: Dawn
 * only rewrites it from `toggleSubmitButton`, which always writes `disabled` in
 * the same call, so the attribute mutation is a complete trigger.
 *
 * ── 2. `PUB_SUB_EVENTS.cartUpdate` ──
 * Dawn publishes it from `product-form.js` after a successful add. The observed
 * button is RE-RESOLVED (a section re-render can replace the node while a
 * request is in flight, which would otherwise strand the sticky button in a
 * permanent loading state — the abandonment risk this unit exists to remove)
 * and the state is re-synced from scratch, so a missed mutation cannot survive.
 *
 * ── 3. REVEAL ON PRIMARY-ATC EXIT ──
 * 02 §7 defect 3: the comp's bar is visible from scroll 0, competing with an
 * ATC ~600px below and permanently eating 9% of the viewport. One
 * IntersectionObserver on the primary ATC; shown only once that button has left
 * the viewport UPWARDS (`boundingClientRect.bottom <= 0`), so it is absent at
 * scroll 0 and while the shopper is still scrolling DOWN toward the buy box.
 * The transition is transform-only and lives in CSS, which is where
 * `prefers-reduced-motion` disables it (02 §10 item 12).
 *
 * ── SELECTION READBACK ──
 * The bar must show the SELECTED variant and plan. `new FormData(form)`
 * collects every form-ASSOCIATED control — the shade radios, the purchase-option
 * radios and the `selling_plan` select all sit OUTSIDE the form element and
 * reach it through `form=`. A disabled `selling_plan` select is excluded by the
 * platform, which is the "omit, never send 0" contract. The `change` listener is
 * on `document`, filtered by `event.target.form === this.form`, because those
 * controls are not descendants of the form and never bubble to it. Amounts come
 * pre-formatted from the Liquid JSON island.
 *
 * ── LIFECYCLE ──
 * `DMElement` (dm-core.js): the document listener through `this.on()` (the
 * mount's AbortController), the subscription through `this.sub()`, EVERY
 * observer through `this.track()`. A theme-editor subtree swap tears down every
 * one of them. Nothing binds `shopify:section:load`; nothing caches a rect.
 *
 * Classic script, ES2020, defer-loaded by the snippet.
 */
(function () {
  'use strict';

  /** The only attributes Dawn mutates on its submit button that matter here. */
  const MIRRORED_ATTRIBUTES = ['disabled', 'aria-disabled', 'class'];

  /** Shopify's consent banner, and the token that moves the bar above it. */
  const CONSENT_BANNER = 'shopify-pc__banner';
  const CONSENT_OFFSET = '--dm-consent-offset';

  class DMStickyAtc extends DMElement {
    init() {
      this.form = document.getElementById(this.str('formId'));
      this.button = this.querySelector('[data-dm-sticky-submit]');

      // No form to submit into means the bar cannot do the one thing it is for.
      // Staying hidden is the honest answer (governing rule 3).
      if (!this.form || !this.button) return;

      this.label = this.button.querySelector('.dm-btn__label');
      this.spinner = this.button.querySelector('.loading__spinner');
      this.variantEl = this.querySelector('[data-dm-sticky-variant]');
      this.planEl = this.querySelector('[data-dm-sticky-plan]');
      this.priceEl = this.querySelector('[data-dm-sticky-price]');
      this.data = this.readIsland();

      // ONE MutationObserver for the life of the mount. `bindMirror()` only
      // ever re-points it; it never creates a second one.
      this.mirror = this.track(new MutationObserver(() => this.syncSubmit()));
      this.source = null;
      this.bindMirror();

      this.sub(PUB_SUB_EVENTS.cartUpdate, () => {
        this.bindMirror();
        this.syncSubmit();
        this.syncSelection();
      });

      this.on(document, 'change', (event) => {
        const target = event.target;
        if (!target || target.form !== this.form) return;
        this.frame(() => this.syncSelection());
      });

      this.bindReveal();
      this.bindOffset();
      this.syncSubmit();
      this.syncSelection();
    }

    /* ── 1. the mirror ── */

    /** Point the single observer at the form's current submit button. Idempotent:
     *  re-run on every cartUpdate, a no-op unless the node actually changed. */
    bindMirror() {
      const source = this.form.querySelector('[type="submit"]');
      if (!source || source === this.source) return;
      this.source = source;
      this.mirror.disconnect();
      this.mirror.observe(source, { attributes: true, attributeFilter: MIRRORED_ATTRIBUTES });
    }

    syncSubmit() {
      const source = this.source;
      if (!source) return;

      const loading = source.classList.contains('loading');

      // The two mean different things in Dawn and are mirrored separately:
      // `disabled` is sold-out/unavailable (toggleSubmitButton); `aria-disabled`
      // is in-flight, and is the guard that makes a double submit a no-op.
      this.attr(this.button, 'disabled', source.hasAttribute('disabled'));
      this.attr(this.button, 'aria-disabled', source.getAttribute('aria-disabled') === 'true' ? 'true' : null);

      // Dawn's own class names, so one set of rules styles both buttons.
      this.button.classList.toggle('loading', loading);
      if (this.spinner) this.spinner.classList.toggle('hidden', !loading);

      const sourceLabel = source.querySelector('span');
      if (this.label && sourceLabel) {
        const text = sourceLabel.textContent.trim();
        if (text && this.label.textContent !== text) this.label.textContent = text;
      }
    }

    /* ── 2. selection readback ── */

    readIsland() {
      const node = this.querySelector('[data-dm-sticky-prices]');
      if (!node) return null;
      try {
        return JSON.parse(node.textContent);
      } catch (error) {
        console.error(this.tagName.toLowerCase(), error);
        return null;
      }
    }

    syncSelection() {
      const data = this.data;
      if (!data || !data.variants) return;

      const fields = new FormData(this.form);
      // The hidden `[name=id]` carries `disabled` while the selected variant is
      // unavailable, and a disabled control is excluded from FormData — so read
      // its value directly rather than blanking the bar.
      const idInput = this.form.querySelector('[name="id"]');
      const id = String(fields.get('id') || (idInput && idInput.value) || '');
      const plan = String(fields.get('selling_plan') || '');

      const variant = data.variants[id];
      if (!variant) return;

      if (this.variantEl) this.variantEl.textContent = variant.l || '';
      if (this.planEl) this.planEl.textContent = (data.plans && data.plans[plan]) || '';
      if (this.priceEl) {
        // Falls back to the one-time price whenever the chosen plan has no
        // allocation on this variant — a real state while a shopper changes
        // shade with subscribe still selected.
        const prices = variant.p || {};
        const text = prices[plan] || prices[''] || '';
        if (text) this.priceEl.textContent = text;
      }
    }

    /* ── 3. the reveal ── */

    bindReveal() {
      // Merchants must see what they edit (dm-core.js): in the theme editor the
      // bar is revealed immediately, because scrolling a preview iframe to find
      // a button that may be hidden is not a review.
      if (this.editing || this.str('reveal') === 'immediately' || !('IntersectionObserver' in window)) {
        this.show(true);
        return;
      }

      const target = document.getElementById(this.str('revealId'));
      if (!target) {
        // No anchor to reveal behind. Showing is the safe failure: the bar still
        // submits the right form, and hiding it forever would remove a
        // conversion control with no signal that anything was wrong.
        this.show(true);
        return;
      }

      this.track(
        new IntersectionObserver(
          ([entry]) => {
            this.show(!entry.isIntersecting && entry.boundingClientRect.bottom <= 0);
          },
          { threshold: 0 }
        )
      ).observe(target);
    }

    /** `data-shown` is on §9.11's fixed CSS↔JS interface list. The transform,
     *  the visibility and the reduced-motion opt-out are all CSS. */
    show(shown) {
      this.attr(this, 'data-shown', shown ? 'true' : null);
    }

    /** ── 4. the consent offset (PLAN Phase 5) ──
     *  The banner WINS the bottom of the viewport until dismissed, never fought
     *  with z-index; rationale in full at the `--dm-consent-offset` declaration
     *  in `component-dm-sticky-bar.css`. The privacy script injects the banner
     *  AFTER first paint, so a `childList` observer waits for it. Both go
     *  through `this.track()` — one teardown. `removeProperty`, never a written
     *  0px, hands the value back to that stylesheet's own `:root`.
     *  It STAYS CONNECTED: the banner is removed on accept and RE-CREATED when
     *  a shopper reopens "Manage cookie preferences", so a one-shot observer
     *  would miss the second. `seen` makes other body mutations a no-op. */
    bindOffset() {
      const style = document.documentElement.style;
      const write = () => {
        const el = document.getElementById(CONSENT_BANNER);
        const h = el && this.isConnected ? Math.round(el.getBoundingClientRect().height) : 0;
        if (h > 0) style.setProperty(CONSENT_OFFSET, `${h}px`);
        else style.removeProperty(CONSENT_OFFSET);
      };
      const ro = this.track(new ResizeObserver(() => this.frame(write)));
      let seen;
      const watch = () => {
        const el = document.getElementById(CONSENT_BANNER);
        if (el === seen) return;
        seen = el;
        ro.disconnect();
        if (el) ro.observe(el);
        write();
      };
      this.track(new MutationObserver(watch)).observe(document.body, { childList: true });
      watch(); // a banner already in the DOM must move the bar in THIS frame
    }
  }

  DMElement.define('dm-sticky-atc', DMStickyAtc);
})();
